import { Icon } from '@iconify/react'

import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'
import BrowseProjectsIcon from '../../assets/icons/browseProjectsIcon'

// antd
import { Dropdown, Popover, Skeleton, Tooltip, Typography } from 'antd'

// slices
import {
    fetchProjectDetail,
    projectsListForClientModal,
    projectsState,
    resetClientModalProjects,
    setActiveProjectSupport,
    updateProject,
} from '../../modules/projects/projects.slice'

// css
import styles from './projectsList.module.css'
import { PRIVATE_ROUTES, buildProjectTaskTabSearch } from '../../routes'
import { loginState } from '../../modules/login/login.slice'
import CreateProjectModal from '../../modules/projects/components/CreateProjectModal'
import ProjectColorIconFlyout from '../../modules/projects/components/ProjectColorIconFlyout'
import { DEFAULT_COLOR_PALETTE } from '../../constants/projects'
import { fetchJiraOauthStatus } from '../../modules/jira/jira.slice'
import { showActionNotification } from '../../components/notification'
import { harmonicIconToneForBackground } from '../../helpers/harmonicIconColor'

const { Text } = Typography

const getActiveProjectIdFromPath = (pathname = '') => {
    const match = pathname.match(/\/dashboard\/projects\/([^/]+)/)
    return match?.[1] || null
}

const copyToClipboard = async (text) => {
    try {
        if (!text) return false
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return true
        }
    } catch {
        // fall through
    }
    try {
        const el = document.createElement('textarea')
        el.value = String(text)
        el.setAttribute('readonly', '')
        el.style.position = 'fixed'
        el.style.top = '-1000px'
        el.style.left = '-1000px'
        document.body.appendChild(el)
        el.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(el)
        return ok
    } catch {
        return false
    }
}

const ProjectsList = ({ collapsed }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const [createProjectOpen, setCreateProjectOpen] = useState(false)
    const { userProfile } = useSelector(loginState)

    const {
        clientModalListStatus,
        clientModalProjectsListData = [],
        clientModalProjectPage,
        clientModalProjectSearch,
        clientModalIsNext,
        clientModalTotalProjects,
        projectDetail,
    } = useSelector(projectsState)
    const isSuperOrExistingUser = Boolean(
        userProfile?.isSuperAdmin || userProfile?.isExistingUser
    )
    const showBrowseProjectsButton =
        isSuperOrExistingUser ||
        (userProfile?.isSupportUser && Number(clientModalTotalProjects) > 5)
    const isBrowseProjectsActive =
        location.pathname === PRIVATE_ROUTES.projects.root

    const activeProjectId = useMemo(
        () => getActiveProjectIdFromPath(location.pathname),
        [location.pathname]
    )

    const sidebarProjects = useMemo(() => {
        const list = clientModalProjectsListData || []
        const pinned = list.slice(0, 6)
        if (!activeProjectId) return pinned

        if (pinned.some((project) => project?._id === activeProjectId)) {
            return pinned
        }

        const activeProject =
            list.find((project) => project?._id === activeProjectId) ||
            (projectDetail?._id === activeProjectId ? projectDetail : null)

        if (!activeProject) return pinned

        return [...pinned.slice(0, 5), activeProject]
    }, [clientModalProjectsListData, activeProjectId, projectDetail])

    useEffect(() => {
        dispatch(resetClientModalProjects())
        dispatch(
            projectsListForClientModal({
                skip: 0,
                filter: {},
                limit: 10,
                search: '',
                userProfile: userProfile,
            })
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch])

    const fetchMoreProjects = () => {
        if (clientModalIsNext && clientModalListStatus !== 'loading') {
            const nextPage = clientModalProjectPage + 1
            const skip = (nextPage - 1) * 10

            dispatch(
                projectsListForClientModal({
                    skip: skip,
                    filter: clientModalProjectSearch
                        ? { search: clientModalProjectSearch }
                        : {},
                    limit: 10,
                    search: clientModalProjectSearch,
                    userProfile: userProfile,
                })
            )
        }
    }

    const handleProjectClick = async (projectId, e) => {
        if (e.ctrlKey || e.metaKey) {
            return
        }

        if (!userProfile?.isSuperAdmin) {
            dispatch(
                setActiveProjectSupport({
                    projectId,
                })
            )
        }
        if (userProfile?.isExistingUser) {
            navigate({
                pathname: PRIVATE_ROUTES.projects.projectIdView(
                    projectId,
                    'board'
                ),
                search: buildProjectTaskTabSearch('development'),
            })
        } else {
            navigate(PRIVATE_ROUTES.projects.projectId(projectId))
        }
    }

    const handleArchiveToggle = async (project, e) => {
        e?.stopPropagation?.()
        const projectId = project?._id
        if (!projectId) return

        const nextArchivedValue = !Boolean(project?.isArchived)
        const result = await dispatch(
            updateProject({
                _id: projectId,
                updatedData: {
                    isArchived: nextArchivedValue,
                },
            })
        )

        if (result?.payload?.data?.success) {
            showActionNotification({
                message: nextArchivedValue
                    ? 'Project archived successfully'
                    : 'Project unarchived successfully',
                subMessage: project?.name,
                notificationKey: nextArchivedValue
                    ? 'sidebar-project-archived'
                    : 'sidebar-project-unarchived',
            })

            dispatch(resetClientModalProjects())
            dispatch(
                projectsListForClientModal({
                    skip: 0,
                    filter: {},
                    limit: 10,
                    search: clientModalProjectSearch || '',
                    userProfile,
                })
            )

            if (projectDetail?._id === projectId) {
                dispatch(fetchProjectDetail({ id: projectId }))
            }
        } else {
            showActionNotification({
                type: 'error',
                message: nextArchivedValue
                    ? 'Could not archive project'
                    : 'Could not unarchive project',
                subMessage:
                    result?.payload?.data?.message || 'Please try again.',
                notificationKey: 'sidebar-project-archive-error',
            })
        }
    }

    // Search is currently disabled in UI (input commented out).

    if (collapsed) {
        return null
    }

    return (
        <div className={styles.projectsListContainer}>
            <div className={styles.projectsHeader}>
                <Text className={styles.projectsTitle}>Projects</Text>
                {userProfile &&
                    (userProfile?.isSuperAdmin ||
                        userProfile?.isExistingUser) && (
                        <Tooltip title="Create Project" placement="top">
                            <button
                                type="button"
                                className={styles.addProjectButton}
                                onClick={async () => {
                                    const result = await dispatch(
                                        fetchJiraOauthStatus()
                                    )
                                    const data = result?.payload?.data || {}
                                    const connected = Boolean(
                                        data?.data?.connected
                                    )

                                    if (!connected) {
                                        showActionNotification({
                                            type: 'warning',
                                            message: 'Jira is not connected',
                                            subMessage:
                                                'Connect Jira first to create a project.',
                                            notificationKey:
                                                'jira-not-connected',
                                        })
                                        return
                                    }

                                    setCreateProjectOpen(true)
                                }}
                                aria-label="Create Project"
                            >
                                <Icon
                                    icon="mdi:plus"
                                    className={styles.addProjectIcon}
                                />
                            </button>
                        </Tooltip>
                    )}
            </div>

            <div className={styles.projectsList}>
                {clientModalListStatus === 'loading' &&
                clientModalProjectPage === 1 &&
                clientModalProjectsListData?.length === 0 ? (
                    <div className={styles.skeletonContainer}>
                        {[...Array(5)].map((_, index) => (
                            <div key={index} className={styles.skeletonItem}>
                                <Skeleton.Avatar
                                    active
                                    size={20}
                                    shape="square"
                                    className={styles.skeletonAvatar}
                                />
                                <Skeleton.Input
                                    active
                                    size="small"
                                    className={styles.skeletonText}
                                />
                            </div>
                        ))}
                    </div>
                ) : clientModalProjectsListData?.length === 0 &&
                  clientModalListStatus === 'loaded' ? (
                    <div className={styles.emptyState}>
                        <Icon
                            icon="mdi:folder-multiple-outline"
                            className={styles.emptyIcon}
                        />
                        <Text className={styles.emptyText}>
                            No projects found
                        </Text>
                    </div>
                ) : (
                    <InfiniteScroll
                        dataLength={clientModalProjectsListData?.length}
                        next={fetchMoreProjects}
                        hasMore={clientModalIsNext}
                        loader={
                            <div className={styles.loadingContainer}>
                                <Icon
                                    icon="line-md:loading-loop"
                                    style={{ fontSize: 20, color: '#ffffff' }}
                                />
                            </div>
                        }
                        height={'auto'}
                        style={{
                            scrollbarWidth: '2px',
                            scrollbarColor: '#888 transparent',
                        }}
                    >
                        {sidebarProjects.length > 0 &&
                            sidebarProjects.map((project) => {
                                const isActive =
                                    Boolean(activeProjectId) &&
                                    project?._id === activeProjectId
                                const projectColor =
                                    project?.colorCode || '#60a5fa'

                                return (
                                    <Dropdown
                                        key={project._id}
                                        trigger={['contextMenu']}
                                        placement="bottomLeft"
                                        overlayClassName={
                                            styles.projectContextDropdown
                                        }
                                        popupRender={() => (
                                            <div
                                                className={
                                                    styles.projectContextMenu
                                                }
                                            >
                                                {userProfile &&
                                                    (userProfile?.isSuperAdmin ||
                                                        userProfile?.isExistingUser) && (
                                                        <Popover
                                                            trigger={['hover']}
                                                            placement="rightTop"
                                                            overlayClassName={
                                                                styles.colorIconPopoverOverlay
                                                            }
                                                            getPopupContainer={(
                                                                triggerNode
                                                            ) =>
                                                                triggerNode?.closest?.(
                                                                    '.ant-dropdown'
                                                                ) ||
                                                                document.body
                                                            }
                                                            content={
                                                                <ProjectColorIconFlyout
                                                                    projectId={
                                                                        project?._id
                                                                    }
                                                                    palette={
                                                                        DEFAULT_COLOR_PALETTE
                                                                    }
                                                                    initialColor={
                                                                        project?.colorCode
                                                                    }
                                                                />
                                                            }
                                                            arrow={false}
                                                        >
                                                            <button
                                                                type="button"
                                                                className={`${styles.projectContextMenuItem} ${styles.projectContextMenuItemRow}`}
                                                                onClick={(e) =>
                                                                    e.stopPropagation()
                                                                }
                                                            >
                                                                <span
                                                                    className={
                                                                        styles.menuColorDot
                                                                    }
                                                                    style={{
                                                                        backgroundColor:
                                                                            project?.colorCode ||
                                                                            projectColor,
                                                                    }}
                                                                    aria-hidden
                                                                />
                                                                <span
                                                                    style={{
                                                                        flex: 1,
                                                                        textAlign:
                                                                            'left',
                                                                    }}
                                                                >
                                                                    Set color &
                                                                    icon
                                                                </span>
                                                                <Icon
                                                                    icon="mdi:chevron-right"
                                                                    width={18}
                                                                    height={18}
                                                                    style={{
                                                                        color: '#94a3b8',
                                                                        flexShrink: 0,
                                                                    }}
                                                                />
                                                            </button>
                                                        </Popover>
                                                    )}

                                                <button
                                                    type="button"
                                                    className={
                                                        styles.projectContextMenuItem
                                                    }
                                                    onClick={(e) => {
                                                        e?.stopPropagation?.()
                                                        window.open(
                                                            PRIVATE_ROUTES.projects.projectId(
                                                                project._id
                                                            ),
                                                            '_blank',
                                                            'noopener,noreferrer'
                                                        )
                                                    }}
                                                >
                                                    <Icon
                                                        icon="fluent:open-48-filled"
                                                        width={16}
                                                        height={16}
                                                        style={{
                                                            color: '#757677',
                                                            marginRight: 8,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <span>Open in new tab</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={
                                                        styles.projectContextMenuItem
                                                    }
                                                    onClick={async (e) => {
                                                        e?.stopPropagation?.()
                                                        const path =
                                                            PRIVATE_ROUTES.projects.projectId(
                                                                project._id
                                                            )
                                                        const url = `${window.location.origin}${path}`
                                                        const ok =
                                                            await copyToClipboard(
                                                                url
                                                            )
                                                        showActionNotification({
                                                            type: ok
                                                                ? 'success'
                                                                : 'error',
                                                            message: ok
                                                                ? 'Project link copied'
                                                                : 'Could not copy link',
                                                            notificationKey:
                                                                'sidebar-project-link-copied',
                                                        })
                                                    }}
                                                >
                                                    <Icon
                                                        icon="mdi:link-variant"
                                                        width={16}
                                                        height={16}
                                                        style={{
                                                            color: '#757677',
                                                            marginRight: 8,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <span>Copy link</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={
                                                        styles.projectContextMenuItem
                                                    }
                                                    onClick={(e) =>
                                                        handleArchiveToggle(
                                                            project,
                                                            e
                                                        )
                                                    }
                                                >
                                                    <Icon
                                                        icon={
                                                            'fluent:archive-20-regular'
                                                        }
                                                        width={16}
                                                        height={16}
                                                        style={{
                                                            color: '#757677',
                                                            marginRight: 8,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <span>
                                                        {project?.isArchived
                                                            ? 'Unarchive Project'
                                                            : 'Archive Project'}
                                                    </span>
                                                </button>
                                            </div>
                                        )}
                                    >
                                        <div
                                            className={`${styles.projectItem} ${
                                                isActive
                                                    ? styles.projectItemActive
                                                    : ''
                                            }`}
                                            onContextMenu={(e) =>
                                                e.preventDefault()
                                            }
                                            onClick={(e) => {
                                                handleProjectClick(
                                                    project._id,
                                                    e
                                                )
                                            }}
                                        >
                                            <div
                                                className={styles.projectIcon}
                                                style={{
                                                    backgroundColor:
                                                        projectColor,
                                                }}
                                            >
                                                {project?.urlType === 'icon' &&
                                                project?.url ? (
                                                    <Icon
                                                        icon={project.url}
                                                        width={15}
                                                        height={15}
                                                        style={{
                                                            color: harmonicIconToneForBackground(
                                                                projectColor
                                                            ),
                                                        }}
                                                    />
                                                ) : project?.urlType ===
                                                      'upload' &&
                                                  project?.url ? (
                                                    <img
                                                        src={project.url}
                                                        alt={project.name}
                                                        className={
                                                            styles.projectIconImage
                                                        }
                                                    />
                                                ) : !project?.colorCode &&
                                                  project?.projectProfile ? (
                                                    <img
                                                        src={
                                                            project.projectProfile
                                                        }
                                                        alt={project.name}
                                                        className={
                                                            styles.projectIconImage
                                                        }
                                                    />
                                                ) : !project?.colorCode ? (
                                                    <Text
                                                        className={
                                                            styles.projectIconText
                                                        }
                                                    >
                                                        {project?.name
                                                            ?.charAt(0)
                                                            .toUpperCase()}
                                                    </Text>
                                                ) : null}
                                            </div>
                                            <Text
                                                className={styles.projectName}
                                                title={project?.name}
                                            >
                                                {project?.name}
                                            </Text>
                                        </div>
                                    </Dropdown>
                                )
                            })}
                    </InfiniteScroll>
                )}
            </div>

            {showBrowseProjectsButton && (
                <button
                    type="button"
                    className={`${styles.browseProjectsBtn} ${
                        isBrowseProjectsActive
                            ? styles.browseProjectsBtnActive
                            : ''
                    }`}
                    onClick={() => navigate(PRIVATE_ROUTES.projects.root)}
                >
                    <span className={styles.browseIcon} aria-hidden="true">
                        <BrowseProjectsIcon />
                    </span>
                    <span className={styles.browseText}>Browse Projects</span>
                </button>
            )}

            <CreateProjectModal
                open={createProjectOpen}
                onCancel={() => setCreateProjectOpen(false)}
            />
        </div>
    )
}

export default ProjectsList
